import { useContext, useEffect, useRef, useState } from "react";
import HxGNConnect from "../../../../assets/images/hxgn-connect.png";
import HxGNOnCall from "../../../../assets/images/hxgn-oncall.png";
import AlertTone from "../../../../assets/audio/alert-tone.mp3";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import "../../../../styles/global.css";
import { SignalRContext } from "@/contexts/SignalRContext";
import type { AlertData } from "@/features/alerts/types/alerts.types";
import toastService from "@/shared/components/common/toast/toastService";
import NotificationCard from "./NotificationCard";

interface NotificationPanelProps {
  activeBtn: string | null;
  onSelect: (value: string) => void;
}

function NotificationPanel({ activeBtn, onSelect }: NotificationPanelProps) {
  const { connection } = useContext(SignalRContext);
  const [notyCount, setNotyCount] = useState<number>(0);
  const [alerts, setAlerts] = useState<AlertData[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const titleSuffix = import.meta.env.VITE_TITLE_SUFFIX || "";
  useEffect(() => {
    setIsOpen(activeBtn === "notification");
  }, [activeBtn]);
  const [showNotification, setShowNotification] = useState<boolean>(true);

  const getLogo = (ProjetName: string) => {
    return ProjetName === "OnCall" ? HxGNOnCall : HxGNConnect;
  };

  useEffect(() => {
    let audio: HTMLAudioElement | null = null;

    try {
      audio = new Audio(AlertTone);
      audio.onerror = () => console.warn("Failed to load alert audio file.");
    } catch (err) {
      console.warn("Audio initialization failed:", err);
    }

    const IsNotify = localStorage.getItem("IsNotify");
    if (IsNotify !== null) {
      setShowNotification(IsNotify.toLowerCase() === "true");
    }
    if (!connection) return;

    const playAudio = () => {
      if (!audio) return;
      audio.play().catch((err: DOMException) => {
        if (err.name === "NotAllowedError") {
          console.warn("Audio autoplay blocked by browser policy.");
        } else {
          console.warn("Audio playback failed:", err.message);
        }
      });
    };

    const showBrowserNotification = (title: string, options: NotificationOptions) => {
      if (!("Notification" in window)) return;

      const fire = () => {
        try {
          new window.Notification(title, options);
        } catch (err) {
          console.warn("Browser notification failed:", err);
        }
      };

      if (Notification.permission === "granted") {
        fire();
      } else if (Notification.permission !== "denied") {
        Notification.requestPermission().then((permission) => {
          if (permission === "granted") fire();
        });
      }
    };

    const handleAlert = (Alert: any) => {
      const AlertData: AlertData = {
        alertName: Alert.alertName,
        severity: Alert.severity,
        firedTime: Alert.firedTime,
        customer: Alert.customer,
        useCase: Alert.useCase,
        product: Alert.product,
        alertUrl: Alert.alertUrl,
        project: Alert.project,
        isSubscribed: Alert.isSubscribed,
      };

      setNotyCount((prev) => prev + 1);
      setAlerts((prev) => [...prev, AlertData]);

      if (showNotification) {
        playAudio();
        toastService.ShowToast(AlertData);

        const customerinfo = `${(Alert.customer ?? "").toUpperCase()}/${(Alert.useCase ?? "").toUpperCase()}/${(Alert.product ?? "").toUpperCase()}`;
        showBrowserNotification(customerinfo, {
          body: `Alert Name: ${Alert.alertName}\nSeverity: ${Alert.severity === "Informational" ? "Info" : Alert.severity}\nFired Time: ${Alert.firedTime} (UTC)`,
        });
      }
    };

    connection.on("Alerts", handleAlert);
    return () => connection.off("Alerts", handleAlert);
  }, [connection, showNotification]);

  useEffect(() => {
    document.title =
      notyCount > 0
        ? `(${notyCount}) Cloud Notification Center ${titleSuffix}`
        : `Cloud Notification Center ${titleSuffix}`;
  }, [notyCount]);

  const setNotification = () => {
    setShowNotification((prev) => {
      const updated = !prev;
      localStorage.setItem("IsNotify", String(updated));
      return updated;
    });
  };

  dayjs.extend(utc);

  function getDurationFromNow(updatetime?: string | Date): string {
    if (!updatetime) return "";

    const now = dayjs.utc(); // current UTC time
    const updateTime = dayjs.utc(updatetime); // ensure input is parsed as UTC

    const diffInMinutes = now.diff(updateTime, "minute");
    const diffInHours = now.diff(updateTime, "hour");

    return diffInMinutes < 1
      ? "Just now"
      : diffInMinutes < 60
        ? `${diffInMinutes} min ago`
        : `${diffInHours} hr ago`;
  }

  return (
    <div>
      <button
        type="button"
        className="icon-button"
        onClick={() => onSelect("notification")}
      >
        <div className="tooltip-wrapper">
          <i className="fa-solid fa-bell fa-lg text-[var(--text-primary)]"></i>
          <span className="tooltip-text">Notifications</span>
        </div>
        {notyCount > 0 && (
          <span
            className="icon-button__badge"
            style={{
              backgroundColor: "var(--severity-info)",
              border: "1px solid var(--severity-info)",
              color: "var(--btn-danger-text)",
              minWidth: "20px",
              height: "20px",
              padding: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              fontSize: "0.75rem"
            }}
          >
            {notyCount}
          </span>
        )}
      </button>

      <div className="notification-overlay">
        <div
          ref={sidebarRef}
          className={`fixed top-[85px] right-0 h-[calc(100vh-64px)] bg-[var(--bg-surface)] border-l border-[var(--border-default)] shadow-md 
								transform transition-transform duration-300 ease-in-out 
								z-[99999]
								${isOpen ? "translate-x-0" : "translate-x-full"}`}
          style={{ width: '320px' }}
        >
          <div className="p-4 font-semibold flex justify-between items-center bg-[var(--bg-surface)] text-[var(--text-primary)] border-b border-[var(--border-default)]">
            <div className="flex flex-row items-center justify-center gap-2">
              {/* <span>Notifications</span> */}

              <span className="text-lg">
                {showNotification
                  ? "🔔 Popup Notifications On"
                  : "🔕 Popup Notifications Off"}
              </span>
              <button
                onClick={() => setNotification()}
                className={`relative inline-flex h-6 w-11 items-center rounded-full border transition-colors duration-300 cursor-pointer ${
                  showNotification
                    ? "bg-[var(--status-success)] border-[var(--status-success)]"
                    : "bg-[var(--switch-track-off)] border-[var(--switch-track-off)]"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full border border-[var(--border-default)] bg-[var(--switch-handle)] shadow-sm transition-transform duration-300 cursor-pointer ${
                    showNotification ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            <button
              onClick={() => onSelect("notification")}
              className="text-xl cursor-pointer text-[var(--text-primary)]"
            >
              &times;
            </button>
          </div>

          <div className="p-2 flex flex-col justify-between h-[84vh]">
            <div
              className={`flex-grow space-y-3 pr-1 ${alerts.length > 0 ? "overflow-y-auto" : ""}`}
            >
              {alerts.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-[var(--text-tertiary)] text-sm mt-10">
                  <i className="pi pi-bell-off text-3xl mb-3 text-[var(--text-tertiary)]" />
                  <p>No new notifications</p>
                </div>
              ) : (
                alerts
                  .slice()
                  .reverse()
                  .map((item, index) => (
                    <NotificationCard
                      key={index}
                      icon={getLogo(item.project!)}
                      title={
                        item.severity! == "Critical"
                          ? "Critical"
                          : item.severity! == "Error"
                            ? "Error"
                            : item.severity! == "Warning"
                              ? "Warning"
                              : "Info"
                      }
                      details={
                        item.customer?.toUpperCase() +
                        "/" +
                        item.useCase?.toUpperCase() +
                        "/" +
                        item.product?.toUpperCase()
                      }
                      description=""
                      alertName={item.alertName!}
                      link={item.alertUrl!}
                      timeAgo={getDurationFromNow(item.firedTime)}
                    />
                  ))
              )}
            </div>
            <div className="w-full py-6 flex justify-center">
              <button
                onClick={() => {
                  setAlerts([]);
                  setNotyCount(0);
                }}
                className="w-70 mb-3 group flex items-center justify-center  gap-2 px-5 py-2 rounded-lg bg-[var(--bg-surface)] border border-[var(--border-default)] shadow hover:bg-[var(--bg-elevated)] transition duration-300"
              >
                <i className="pi pi-trash text-[var(--status-error)] group-hover:scale-110 transition-transform duration-300"></i>
                <span className="text-sm text-[var(--text-primary)] font-medium">
                  Clear All
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NotificationPanel;
